import { useCallback, useMemo, useState, type CSSProperties, type ReactNode } from 'react';
import { useAssistant, useTheme, useAccountConfig, type ThemeTokens } from '@course-authoring/shared-types';
import { Trans, useTranslation, type TFunction } from '@course-authoring/i18n';
import { createContentBlockStyles } from './styles/contentBlocks';
import { useDialogA11y } from './hooks/useDialogA11y';
import { useIsMobileViewport } from './hooks/useIsMobileViewport';
import emaAvatar from './assets/ema.png';

export interface WelcomeModalProps {
  courseTitle: string;
  isTimed?: boolean;
  minimumRequiredMinutes?: number | null;
  /** Overrides the account config's `enableAskExpert` when provided. */
  enableAskExpert?: boolean;
  onDismiss: () => void;
}

interface ScreenDef {
  items: { icon: ReactNode; text: ReactNode }[];
}

const IconPanel = ({ color, size = 32 }: { color: string; size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 30 27" fill="none">
    <rect width="10" height="26.3637" rx="2" fill={color} />
    <rect width="10" height="26.3637" rx="2" fill="black" fillOpacity="0.2" />
    <line x1="14.2273" y1="4.86365" x2="28.5001" y2="4.86365" stroke={color} strokeWidth="3" strokeLinecap="round" />
    <line x1="14.2273" y1="4.86365" x2="28.5001" y2="4.86365" stroke="black" strokeOpacity="0.2" strokeWidth="3" strokeLinecap="round" />
    <line x1="14.2273" y1="10.3182" x2="28.5001" y2="10.3182" stroke={color} strokeWidth="3" strokeLinecap="round" />
    <line x1="14.2273" y1="10.3182" x2="28.5001" y2="10.3182" stroke="black" strokeOpacity="0.2" strokeWidth="3" strokeLinecap="round" />
    <line x1="14.2273" y1="15.7728" x2="28.5001" y2="15.7728" stroke={color} strokeWidth="3" strokeLinecap="round" />
    <line x1="14.2273" y1="15.7728" x2="28.5001" y2="15.7728" stroke="black" strokeOpacity="0.2" strokeWidth="3" strokeLinecap="round" />
    <line x1="14.2273" y1="21.2273" x2="21.2273" y2="21.2273" stroke={color} strokeWidth="3" strokeLinecap="round" />
    <line x1="14.2273" y1="21.2273" x2="21.2273" y2="21.2273" stroke="black" strokeOpacity="0.2" strokeWidth="3" strokeLinecap="round" />
  </svg>
);

const IconContent = ({ color, size = 32 }: { color: string; size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 34 34" fill="none">
    <path d="M15.4395 0.343686C16.4289 -0.114562 17.5711 -0.114562 18.5605 0.343686L33.077 7.05137C33.6414 7.31038 34 7.87489 34 8.49917C34 9.12345 33.6414 9.68796 33.077 9.94697L18.5605 16.6547C17.5711 17.1129 16.4289 17.1129 15.4395 16.6547L0.923047 9.94697C0.358594 9.68132 0 9.11681 0 8.49917C0 7.88153 0.358594 7.31038 0.923047 7.05137L15.4395 0.343686ZM3.19414 14.5029L14.1047 19.5436C15.9441 20.3937 18.0625 20.3937 19.902 19.5436L30.8125 14.5029L33.077 15.5522C33.6414 15.8112 34 16.3757 34 17C34 17.6243 33.6414 18.1888 33.077 18.4478L18.5605 25.1555C17.5711 25.6137 16.4289 25.6137 15.4395 25.1555L0.923047 18.4478C0.358594 18.1821 0 17.6176 0 17C0 16.3824 0.358594 15.8112 0.923047 15.5522L3.1875 14.5029H3.19414ZM0.923047 24.053L3.1875 23.0037L14.098 28.0444C15.9375 28.8945 18.0559 28.8945 19.8953 28.0444L30.8059 23.0037L33.0703 24.053C33.6348 24.312 33.9934 24.8766 33.9934 25.5008C33.9934 26.1251 33.6348 26.6896 33.0703 26.9486L18.5539 33.6563C17.5645 34.1146 16.4223 34.1146 15.4328 33.6563L0.923047 26.9486C0.358594 26.683 0 26.1185 0 25.5008C0 24.8832 0.358594 24.312 0.923047 24.053Z" fill={color} />
    <path d="M15.4395 0.343686C16.4289 -0.114562 17.5711 -0.114562 18.5605 0.343686L33.077 7.05137C33.6414 7.31038 34 7.87489 34 8.49917C34 9.12345 33.6414 9.68796 33.077 9.94697L18.5605 16.6547C17.5711 17.1129 16.4289 17.1129 15.4395 16.6547L0.923047 9.94697C0.358594 9.68132 0 9.11681 0 8.49917C0 7.88153 0.358594 7.31038 0.923047 7.05137L15.4395 0.343686ZM3.19414 14.5029L14.1047 19.5436C15.9441 20.3937 18.0625 20.3937 19.902 19.5436L30.8125 14.5029L33.077 15.5522C33.6414 15.8112 34 16.3757 34 17C34 17.6243 33.6414 18.1888 33.077 18.4478L18.5605 25.1555C17.5711 25.6137 16.4289 25.6137 15.4395 25.1555L0.923047 18.4478C0.358594 18.1821 0 17.6176 0 17C0 16.3824 0.358594 15.8112 0.923047 15.5522L3.1875 14.5029H3.19414ZM0.923047 24.053L3.1875 23.0037L14.098 28.0444C15.9375 28.8945 18.0559 28.8945 19.8953 28.0444L30.8059 23.0037L33.0703 24.053C33.6348 24.312 33.9934 24.8766 33.9934 25.5008C33.9934 26.1251 33.6348 26.6896 33.0703 26.9486L18.5539 33.6563C17.5645 34.1146 16.4223 34.1146 15.4328 33.6563L0.923047 26.9486C0.358594 26.683 0 26.1185 0 25.5008C0 24.8832 0.358594 24.312 0.923047 24.053Z" fill="black" fillOpacity="0.2" />
  </svg>
);

const IconAudio = ({ color, size = 32 }: { color: string; size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 27 22" fill="none">
    <path d="M2.41859 15.7143H4.83717L11.5941 21.5679C11.9166 21.8478 12.3298 22 12.758 22C13.7255 22 14.5115 21.2339 14.5115 20.2911V1.70893C14.5115 0.766071 13.7255 0 12.758 0C12.3298 0 11.9166 0.152232 11.5941 0.432143L4.83717 6.28571H2.41859C1.08333 6.28571 0 7.34152 0 8.64286V13.3571C0 14.6585 1.08333 15.7143 2.41859 15.7143ZM18.4921 7.02232C18.0185 7.48393 18.0185 8.23036 18.4921 8.68705L20.8603 10.9951L18.4921 13.3031C18.0185 13.7647 18.0185 14.5112 18.4921 14.9679C18.9658 15.4246 19.7316 15.4295 20.2002 14.9679L22.5684 12.6598L24.9366 14.9679C25.4103 15.4295 26.1762 15.4295 26.6448 14.9679C27.1134 14.5062 27.1184 13.7598 26.6448 13.3031L24.2766 10.9951L26.6448 8.68705C27.1184 8.22545 27.1184 7.47902 26.6448 7.02232C26.1711 6.56562 25.4052 6.56071 24.9366 7.02232L22.5684 9.33036L20.2002 7.02232C19.7266 6.56071 18.9607 6.56071 18.4921 7.02232Z" fill={color} />
    <path d="M2.41859 15.7143H4.83717L11.5941 21.5679C11.9166 21.8478 12.3298 22 12.758 22C13.7255 22 14.5115 21.2339 14.5115 20.2911V1.70893C14.5115 0.766071 13.7255 0 12.758 0C12.3298 0 11.9166 0.152232 11.5941 0.432143L4.83717 6.28571H2.41859C1.08333 6.28571 0 7.34152 0 8.64286V13.3571C0 14.6585 1.08333 15.7143 2.41859 15.7143ZM18.4921 7.02232C18.0185 7.48393 18.0185 8.23036 18.4921 8.68705L20.8603 10.9951L18.4921 13.3031C18.0185 13.7647 18.0185 14.5112 18.4921 14.9679C18.9658 15.4246 19.7316 15.4295 20.2002 14.9679L22.5684 12.6598L24.9366 14.9679C25.4103 15.4295 26.1762 15.4295 26.6448 14.9679C27.1134 14.5062 27.1184 13.7598 26.6448 13.3031L24.2766 10.9951L26.6448 8.68705C27.1184 8.22545 27.1184 7.47902 26.6448 7.02232C26.1711 6.56562 25.4052 6.56071 24.9366 7.02232L22.5684 9.33036L20.2002 7.02232C19.7266 6.56071 18.9607 6.56071 18.4921 7.02232Z" fill="black" fillOpacity="0.2" />
  </svg>
);

const IconGlobe = ({ color, size = 32 }: { color: string; size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 41 36" fill="none">
    <path d="M28.0595 19.2188H14.8999C15.0998 23.6459 16.0855 27.723 17.4849 30.7088C18.2708 32.3905 19.1187 33.5779 19.9045 34.3055C20.6766 35.0262 21.2074 35.1429 21.4831 35.1429C21.7588 35.1429 22.2896 35.0262 23.0617 34.3055C23.8476 33.5779 24.6955 32.3836 25.4813 30.7088C26.8807 27.723 27.8665 23.6459 28.0664 19.2188H28.0595ZM14.893 15.9241H28.0526C27.8596 11.4969 26.8738 7.41981 25.4744 4.43404C24.6886 2.75926 23.8407 1.56496 23.0548 0.837388C22.2827 0.116685 21.752 0 21.4762 0C21.2005 0 20.6697 0.116685 19.8976 0.837388C19.1118 1.56496 18.2639 2.75926 17.478 4.43404C16.0786 7.41981 15.0929 11.4969 14.893 15.9241ZM11.5841 15.9241C11.8254 10.0487 13.3488 4.59191 15.5754 1.00898C9.22654 3.2466 4.55277 9.00536 3.90479 15.9241H11.5841ZM3.90479 19.2188C4.55277 26.1375 9.22654 31.8963 15.5754 34.1339C13.3488 30.551 11.8254 25.0942 11.5841 19.2188H3.90479ZM31.3683 19.2188C31.1271 25.0942 29.6036 30.551 27.377 34.1339C33.7259 31.8894 38.3997 26.1375 39.0476 19.2188H31.3683ZM39.0476 15.9241C38.3997 9.00536 33.7259 3.2466 27.377 1.00898C29.6036 4.59191 31.1271 10.0487 31.3683 15.9241H39.0476Z" fill={color} />
    <path d="M28.0595 19.2188H14.8999C15.0998 23.6459 16.0855 27.723 17.4849 30.7088C18.2708 32.3905 19.1187 33.5779 19.9045 34.3055C20.6766 35.0262 21.2074 35.1429 21.4831 35.1429C21.7588 35.1429 22.2896 35.0262 23.0617 34.3055C23.8476 33.5779 24.6955 32.3836 25.4813 30.7088C26.8807 27.723 27.8665 23.6459 28.0664 19.2188H28.0595ZM14.893 15.9241H28.0526C27.8596 11.4969 26.8738 7.41981 25.4744 4.43404C24.6886 2.75926 23.8407 1.56496 23.0548 0.837388C22.2827 0.116685 21.752 0 21.4762 0C21.2005 0 20.6697 0.116685 19.8976 0.837388C19.1118 1.56496 18.2639 2.75926 17.478 4.43404C16.0786 7.41981 15.0929 11.4969 14.893 15.9241ZM11.5841 15.9241C11.8254 10.0487 13.3488 4.59191 15.5754 1.00898C9.22654 3.2466 4.55277 9.00536 3.90479 15.9241H11.5841ZM3.90479 19.2188C4.55277 26.1375 9.22654 31.8963 15.5754 34.1339C13.3488 30.551 11.8254 25.0942 11.5841 19.2188H3.90479ZM31.3683 19.2188C31.1271 25.0942 29.6036 30.551 27.377 34.1339C33.7259 31.8894 38.3997 26.1375 39.0476 19.2188H31.3683ZM39.0476 15.9241C38.3997 9.00536 33.7259 3.2466 27.377 1.00898C29.6036 4.59191 31.1271 10.0487 31.3683 15.9241H39.0476Z" fill="black" fillOpacity="0.2" />
  </svg>
);

const IconClock = ({ color, size = 32 }: { color: string; size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 32 32" fill="none">
    <path d="M16 0C24.8375 0 32 7.1625 32 16C32 24.8375 24.8375 32 16 32C7.1625 32 0 24.8375 0 16C0 7.1625 7.1625 0 16 0ZM14.5 7.5V16C14.5 16.5 14.75 16.9688 15.1688 17.25L21.1688 21.25C21.8563 21.7125 22.7875 21.525 23.25 20.8312C23.7125 20.1375 23.525 19.2125 22.8312 18.75L17.5 15.2V7.5C17.5 6.66875 16.8312 6 16 6C15.1687 6 14.5 6.66875 14.5 7.5Z" fill={color} />
    <path d="M16 0C24.8375 0 32 7.1625 32 16C32 24.8375 24.8375 32 16 32C7.1625 32 0 24.8375 0 16C0 7.1625 7.1625 0 16 0ZM14.5 7.5V16C14.5 16.5 14.75 16.9688 15.1688 17.25L21.1688 21.25C21.8563 21.7125 22.7875 21.525 23.25 20.8312C23.7125 20.1375 23.525 19.2125 22.8312 18.75L17.5 15.2V7.5C17.5 6.66875 16.8312 6 16 6C15.1687 6 14.5 6.66875 14.5 7.5Z" fill="black" fillOpacity="0.2" />
  </svg>
);

const IconTimer = ({ color, size = 32 }: { color: string; size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 20 27" fill="none">
    <path d="M1.66667 0C0.744792 0 0 0.754102 0 1.6875C0 2.6209 0.744792 3.375 1.66667 3.375V3.95508C1.66667 6.19102 2.54687 8.33731 4.10937 9.91934L7.64583 13.5L4.10937 17.0807C2.54687 18.6627 1.66667 20.809 1.66667 23.0449V23.625C0.744792 23.625 0 24.3791 0 25.3125C0 26.2459 0.744792 27 1.66667 27H18.3333C19.2552 27 20 26.2459 20 25.3125C20 24.3791 19.2552 23.625 18.3333 23.625V23.0449C18.3333 20.809 17.4531 18.6627 15.8906 17.0807L12.3542 13.5L15.8906 9.91934C17.4531 8.33731 18.3333 6.19102 18.3333 3.95508V3.375C19.2552 3.375 20 2.6209 20 1.6875C20 0.754102 19.2552 0 18.3333 0H1.66667ZM5 3.95508V3.375H15V3.95508C15 5.2998 14.474 6.58652 13.5365 7.53574L10 11.1164L6.46354 7.53574C5.52604 6.58652 5 5.29453 5 3.95508Z" fill={color} />
    <path d="M1.66667 0C0.744792 0 0 0.754102 0 1.6875C0 2.6209 0.744792 3.375 1.66667 3.375V3.95508C1.66667 6.19102 2.54687 8.33731 4.10937 9.91934L7.64583 13.5L4.10937 17.0807C2.54687 18.6627 1.66667 20.809 1.66667 23.0449V23.625C0.744792 23.625 0 24.3791 0 25.3125C0 26.2459 0.744792 27 1.66667 27H18.3333C19.2552 27 20 26.2459 20 25.3125C20 24.3791 19.2552 23.625 18.3333 23.625V23.0449C18.3333 20.809 17.4531 18.6627 15.8906 17.0807L12.3542 13.5L15.8906 9.91934C17.4531 8.33731 18.3333 6.19102 18.3333 3.95508V3.375C19.2552 3.375 20 2.6209 20 1.6875C20 0.754102 19.2552 0 18.3333 0H1.66667ZM5 3.95508V3.375H15V3.95508C15 5.2998 14.474 6.58652 13.5365 7.53574L10 11.1164L6.46354 7.53574C5.52604 6.58652 5 5.29453 5 3.95508Z" fill="black" fillOpacity="0.2" />
  </svg>
);

const IconSaveExit = ({ color, size = 32 }: { color: string; size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 28 28" fill="none">
    <path d="M3.29412 0C1.47721 0 0 1.47721 0 3.29412V23.0588C0 24.8757 1.47721 26.3529 3.29412 26.3529H12.5588C11.3904 24.7316 10.7059 22.7397 10.7059 20.5882C10.7059 15.4103 14.6897 11.1588 19.7647 10.7419V8.78088C19.7647 7.90588 19.4199 7.06691 18.8022 6.44927L13.3154 0.9625C12.6978 0.344853 11.864 0 10.989 0H3.29412ZM16.7537 9.05882H11.9412C11.2566 9.05882 10.7059 8.50809 10.7059 7.82353V3.01103L16.7537 9.05882ZM28 20.5882C28 16.4963 24.6801 13.1765 20.5882 13.1765C16.4963 13.1765 13.1765 16.4963 13.1765 20.5882C13.1765 24.6801 16.4963 28 20.5882 28C24.6801 28 28 24.6801 28 20.5882ZM23.5426 17.4537C23.9081 17.7213 23.9904 18.236 23.7228 18.6015L20.4287 23.1309C20.2846 23.3265 20.0684 23.45 19.8265 23.4654C19.5846 23.4809 19.3478 23.3985 19.1779 23.2287L17.1191 21.1699C16.8 20.8507 16.8 20.3257 17.1191 20.0066C17.4382 19.6875 17.9632 19.6875 18.2824 20.0066L19.6618 21.386L22.3897 17.6338C22.6574 17.2684 23.1721 17.186 23.5426 17.4537Z" fill={color} />
    <path d="M3.29412 0C1.47721 0 0 1.47721 0 3.29412V23.0588C0 24.8757 1.47721 26.3529 3.29412 26.3529H12.5588C11.3904 24.7316 10.7059 22.7397 10.7059 20.5882C10.7059 15.4103 14.6897 11.1588 19.7647 10.7419V8.78088C19.7647 7.90588 19.4199 7.06691 18.8022 6.44927L13.3154 0.9625C12.6978 0.344853 11.864 0 10.989 0H3.29412ZM16.7537 9.05882H11.9412C11.2566 9.05882 10.7059 8.50809 10.7059 7.82353V3.01103L16.7537 9.05882ZM28 20.5882C28 16.4963 24.6801 13.1765 20.5882 13.1765C16.4963 13.1765 13.1765 16.4963 13.1765 20.5882C13.1765 24.6801 16.4963 28 20.5882 28C24.6801 28 28 24.6801 28 20.5882ZM23.5426 17.4537C23.9081 17.7213 23.9904 18.236 23.7228 18.6015L20.4287 23.1309C20.2846 23.3265 20.0684 23.45 19.8265 23.4654C19.5846 23.4809 19.3478 23.3985 19.1779 23.2287L17.1191 21.1699C16.8 20.8507 16.8 20.3257 17.1191 20.0066C17.4382 19.6875 17.9632 19.6875 18.2824 20.0066L19.6618 21.386L22.3897 17.6338C22.6574 17.2684 23.1721 17.186 23.5426 17.4537Z" fill="black" fillOpacity="0.2" />
  </svg>
);

const IconHelp = ({ color, size = 32 }: { color: string; size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 20 31" fill="none">
    <path d="M4 9.6875C4 6.47852 6.6875 3.875 10 3.875C13.3125 3.875 16 6.47852 16 9.6875C16 12.2729 14.2563 14.4646 11.8438 15.2215C10.0688 15.7785 8 17.3588 8 19.8594V21.3125C8 22.3842 8.89375 23.25 10 23.25C11.1062 23.25 12 22.3842 12 21.3125V19.8594C12 19.7564 12.0375 19.6111 12.2188 19.4174C12.4062 19.2176 12.7125 19.0238 13.075 18.9088C17.0938 17.6555 20 14.0045 20 9.6875C20 4.33516 15.525 0 10 0C4.475 0 0 4.33516 0 9.6875C0 10.7592 0.89375 11.625 2 11.625C3.10625 11.625 4 10.7592 4 9.6875ZM10 31C11.3813 31 12.5 29.9162 12.5 28.5781C12.5 27.24 11.3813 26.1562 10 26.1562C8.61875 26.1562 7.5 27.24 7.5 28.5781C7.5 29.9162 8.61875 31 10 31Z" fill={color} />
    <path d="M4 9.6875C4 6.47852 6.6875 3.875 10 3.875C13.3125 3.875 16 6.47852 16 9.6875C16 12.2729 14.2563 14.4646 11.8438 15.2215C10.0688 15.7785 8 17.3588 8 19.8594V21.3125C8 22.3842 8.89375 23.25 10 23.25C11.1062 23.25 12 22.3842 12 21.3125V19.8594C12 19.7564 12.0375 19.6111 12.2188 19.4174C12.4062 19.2176 12.7125 19.0238 13.075 18.9088C17.0938 17.6555 20 14.0045 20 9.6875C20 4.33516 15.525 0 10 0C4.475 0 0 4.33516 0 9.6875C0 10.7592 0.89375 11.625 2 11.625C3.10625 11.625 4 10.7592 4 9.6875ZM10 31C11.3813 31 12.5 29.9162 12.5 28.5781C12.5 27.24 11.3813 26.1562 10 26.1562C8.61875 26.1562 7.5 27.24 7.5 28.5781C7.5 29.9162 8.61875 31 10 31Z" fill="black" fillOpacity="0.2" />
  </svg>
);

const AvatarIcon = ({ bg, src, name, size = 67 }: { bg: string; src: string; name: string; size?: number }) => (
  <div style={{ width: size, height: size, borderRadius: '50%', backgroundColor: bg, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <img
      src={src}
      alt={name}
      width={size}
      height={size}
      style={{ borderRadius: '50%', objectFit: 'cover' }}
      onError={(event) => {
        if (src !== emaAvatar) event.currentTarget.src = emaAvatar;
      }}
    />
  </div>
);

function buildScreens(
  courseTitle: string,
  isTimed: boolean,
  iconColor: string,
  iconSize: number,
  t: TFunction,
  minimumRequiredMinutes?: number | null,
  enableAskExpert?: boolean,
  assistantName = 'Ema',
): ScreenDef[] {
  const screens: ScreenDef[] = [
    {
      items: [
        {
          icon: <IconPanel color={iconColor} size={iconSize} />,
          text: t('welcome_modal.screen1_panel'),
        },
        {
          icon: <IconContent color={iconColor} size={iconSize} />,
          text: t('welcome_modal.screen1_content'),
        },
      ],
    },
    {
      items: [
        {
          icon: <IconAudio color={iconColor} size={iconSize} />,
          text: t('welcome_modal.screen2_audio'),
        },
        {
          icon: <IconGlobe color={iconColor} size={iconSize} />,
          text: t('welcome_modal.screen2_language'),
        },
      ],
    },
  ];

  if (isTimed) {
    const mins = minimumRequiredMinutes ?? 0;
    screens.push({
      items: [
        {
          icon: <IconClock color={iconColor} size={iconSize} />,
          text: (
            <Trans
              i18nKey={
                mins > 0
                  ? 'welcome_modal.screen_timed_assignment'
                  : 'welcome_modal.screen_timed_assignment_required_time'
              }
              values={{ courseTitle, mins }}
              components={{ bold: <strong /> }}
            />
          ),
        },
        {
          icon: <IconTimer color={iconColor} size={iconSize} />,
          text: <Trans i18nKey="welcome_modal.screen_timed_not_a_limit" components={{ bold: <strong /> }} />,
        },
      ],
    });
  }

  screens.push({
    items: [
      {
        icon: <IconSaveExit color={iconColor} size={iconSize} />,
        text: <Trans i18nKey="welcome_modal.screen_save_exit" components={{ bold: <strong /> }} />,
      },
      {
        icon: <IconHelp color={iconColor} size={iconSize} />,
        text: (
          <Trans
            i18nKey={
              enableAskExpert !== false
                ? 'welcome_modal.screen_help'
                : 'welcome_modal.screen_help_no_expert'
            }
            values={{ assistantName }}
            tOptions={{ interpolation: { escapeValue: true } }}
            shouldUnescape
            components={{ bold: <strong /> }}
          />
        ),
      },
    ],
  });

  return screens;
}

export function WelcomeModal({
  courseTitle,
  isTimed = false,
  minimumRequiredMinutes,
  enableAskExpert,
  onDismiss,
}: WelcomeModalProps) {
  const { tokens } = useTheme();
  const { assistant, avatarUrl } = useAssistant();
  const { t } = useTranslation();
  const { enableAskExpert: accountEnableAskExpert } = useAccountConfig();
  const effectiveEnableAskExpert = enableAskExpert ?? accountEnableAskExpert;
  const iconColor = tokens.text.primary;
  const isMobile = useIsMobileViewport(tokens.layout.mobileBreakpoint);
  const isSmallMobile = useIsMobileViewport(tokens.layout.smallMobileBreakpoint);
  const iconSize = isMobile ? 24 : 32;
  const screens = useMemo(() => buildScreens(courseTitle, isTimed, iconColor, iconSize, t, minimumRequiredMinutes, effectiveEnableAskExpert, assistant.name), [courseTitle, isTimed, iconColor, iconSize, t, minimumRequiredMinutes, effectiveEnableAskExpert, assistant.name]);
  const [step, setStep] = useState(0);
  const isFirst = step === 0;
  const isLast = step === screens.length - 1;

  const handleNext = useCallback(() => {
    if (isLast) onDismiss();
    else setStep((s) => s + 1);
  }, [isLast, onDismiss]);

  const handleBack = useCallback(() => {
    setStep((s) => Math.max(0, s - 1));
  }, []);

  const styles = useMemo(() => createStyles(tokens, isMobile, isSmallMobile), [tokens, isMobile, isSmallMobile]);
  const sharedStyles = useMemo(() => createContentBlockStyles(tokens), [tokens]);
  const dialogRef = useDialogA11y<HTMLDivElement>({ onDismiss });

  return (
    <div ref={dialogRef} style={styles.overlay} role="dialog" aria-modal="true" aria-label={t('welcome_modal.title')}>
      <div style={styles.modal}>
        {/* Header banner */}
        <div style={styles.header}>
          <div style={styles.avatar}>
            <AvatarIcon bg={tokens.backgrounds.card} src={avatarUrl ?? emaAvatar} name={assistant.name} size={isMobile ? 54 : 67} />
          </div>
          <div style={styles.headerText}>
            <span style={styles.headerLabel}>{t('welcome_modal.ema_label', { assistantName: assistant.name })}</span>
            <span style={styles.headerTitle}>{t('welcome_modal.title')}</span>
          </div>
        </div>

        {/* Body items */}
        <div style={styles.body}>
          {screens.map((screen, si) => (
            <div key={si} style={{ ...styles.bodyScreen, visibility: si === step ? 'visible' : 'hidden' }}>
              {screen.items.map((item, i) => (
                <div key={i} style={styles.item}>
                  <div style={styles.itemIcon}>{item.icon}</div>
                  <p style={styles.itemText}>{item.text}</p>
                </div>
              ))}
            </div>
          ))}
        </div>

        {/* Footer with dots and nav */}
        <div style={styles.footer}>
          <div style={styles.navRow}>
            <button
              type="button"
              style={isFirst ? styles.backButtonDisabled : styles.backButton}
              onClick={handleBack}
              disabled={isFirst}
              aria-label={t('welcome_modal.aria_back')}
            >
              {t('welcome_modal.back')}
            </button>

            <div style={styles.dots} aria-label={t('welcome_modal.aria_step', { step: step + 1, total: screens.length })}>
              {screens.map((_, i) => (
                <span key={i} style={i === step ? styles.dotActive : styles.dot} aria-hidden="true" />
              ))}
            </div>

            <button
              type="button"
              style={{ ...sharedStyles.actionButton, ...styles.nextButton }}
              onClick={handleNext}
              aria-label={isLast ? t('welcome_modal.aria_next_dismiss') : t('welcome_modal.aria_next')}
            >
              {isLast ? t('welcome_modal.get_started') : t('welcome_modal.next')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function createStyles(tokens: ThemeTokens, isMobile: boolean, isSmallMobile: boolean): Record<string, CSSProperties> {
  const accent = tokens.brand.accent ?? '#5B8EFF';
  const accentDark = tokens.brand.accentHover ?? '#4E80E6';

  return {
    overlay: {
      position: 'fixed',
      inset: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      zIndex: 9999,
    },
    modal: {
      backgroundColor: tokens.backgrounds.page,
      borderRadius: tokens.radius.xl,
      boxShadow: tokens.shadow.lg,
      width: '685px',
      maxWidth: '95vw',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      fontFamily: tokens.typography.fontFamily,
    },
    header: {
      backgroundColor: accent,
      padding: isMobile
        ? tokens.spacing.lgPlus
        : `${tokens.spacing["2xl"]} ${tokens.spacing["3xl"]}`,
      display: 'flex',
      alignItems: 'center',
      gap: tokens.spacing.lg,
    },
    avatar: {
      flexShrink: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    headerText: {
      display: 'flex',
      flexDirection: 'column',
      gap: '2px',
    },
    headerLabel: {
      fontSize: isMobile ? tokens.typography.sizes.ui : tokens.typography.sizes.md,
      fontWeight: tokens.typography.weights.light,
      color: tokens.text.onAccent,
      lineHeight: '1',
    },
    headerTitle: {
      fontSize: isMobile ? tokens.typography.sizes.lg : tokens.typography.sizes.h3,
      fontWeight: tokens.typography.weights.semibold,
      color: tokens.text.onAccent,
      lineHeight: tokens.typography.lineHeights.tight,
    },
    body: {
      padding: isMobile
        ? `${tokens.spacing.xl} ${tokens.spacing.lgPlus}`
        : `${tokens.spacing["3xl"]} ${tokens.spacing["5xl"]} ${tokens.spacing["3xl"]} ${tokens.spacing["3xl"]}`,
      display: 'grid',
    },
    bodyScreen: {
      gridArea: '1 / 1',
      display: 'flex',
      flexDirection: 'column',
      gap: isMobile ? tokens.spacing.xl : tokens.spacing["2xl"],
    },
    item: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: isMobile ? tokens.spacing.lg : tokens.spacing["2xl"],
      minHeight: isMobile ? '66px' : '90px',
    },
    itemIcon: {
      flexShrink: 0,
      width: isMobile ? '44px' : '67px',
      height: isMobile ? '44px' : '67px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: tokens.backgrounds.surfaceAlt,
      borderRadius: tokens.radius.full,
    },
    itemText: {
      margin: 0,
      fontSize: isMobile ? tokens.typography.sizes.ui : tokens.typography.sizes.md,
      fontWeight: tokens.typography.weights.regular,
      lineHeight: tokens.typography.lineHeights.scale,
      color: tokens.text.primary,
    },
    footer: {
      borderTop: `1px solid ${tokens.borders.default}`,
      padding: isMobile
        ? `${tokens.spacing.lg} ${tokens.spacing.lgPlus}`
        : `${tokens.spacing.xl} ${tokens.spacing["3xl"]} ${tokens.spacing.xl}`,
    },
    navRow: {
      display: 'flex',
      alignItems: 'center',
      position: 'relative',
    },
    dots: isMobile
      // In-flow flex item with auto margins: always centered BETWEEN the nav
      // buttons, so the wide Next button can't collide with it.
      ? {
          display: 'flex',
          gap: tokens.spacing.sm,
          alignItems: 'center',
          margin: '0 auto',
        }
      : {
          display: 'flex',
          gap: tokens.spacing.sm,
          alignItems: 'center',
          position: 'absolute',
          left: '43%',
        },
    dot: {
      width: '13px',
      height: '13px',
      borderRadius: tokens.radius.full,
      backgroundColor: tokens.borders.default,
    },
    dotActive: {
      width: '13px',
      height: '13px',
      borderRadius: tokens.radius.full,
      backgroundColor: tokens.backgrounds.dot,
    },
    // Layout overrides on the shared pill. "Get started" is the longest action
    // label in the narrowest container we render one into, so the mobile tier
    // forbids wrapping and compacts the pill instead.
    nextButton: {
      minWidth: '72px',
      marginLeft: isMobile ? undefined : 'auto',
      whiteSpace: 'nowrap',
      ...(isSmallMobile
        ? { fontSize: tokens.typography.sizes.ui, padding: `${tokens.spacing.sm} ${tokens.spacing.lgPlus}` }
        : {}),
    },
    backButton: {
      padding: `${tokens.spacing.sm} ${tokens.spacing.lg}`,
      backgroundColor: 'transparent',
      color: tokens.text.primary,
      border: 'none',
      borderRadius: tokens.radius.sm,
      fontSize: isSmallMobile ? tokens.typography.sizes.ui : tokens.typography.sizes.md,
      fontWeight: tokens.typography.weights.regular,
      fontFamily: tokens.typography.fontFamily,
      cursor: 'pointer',
      minWidth: '48px',
    },
    backButtonDisabled: {
      padding: `${tokens.spacing.sm} ${tokens.spacing.lg}`,
      backgroundColor: 'transparent',
      color: tokens.borders.default,
      border: 'none',
      borderRadius: tokens.radius.sm,
      fontSize: isSmallMobile ? tokens.typography.sizes.ui : tokens.typography.sizes.md,
      fontWeight: tokens.typography.weights.regular,
      fontFamily: tokens.typography.fontFamily,
      cursor: 'default',
      minWidth: '48px',
    },

  };
}
